import React, { useEffect, useMemo } from 'react'

import Modal from '@components/Modal'
import Spinner from '@components/Spinner'
import { successMessages } from '@constants/feedbackMessages'
import { Dialog } from '@headlessui/react'
import { AddTutorialRequest } from '@models/tutorial.model'
import { tutorialApi } from '@services/TutorialApi'
import { closeModal } from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { getCssStyle, pixelsToRem } from '@utils/helpers/styleHelpers'

import TutorialForm, { TutorialFormDefaults } from '../TutorialForm'

const styles = {
	container:
		'relative inline-block align-bottom bg-white rounded-lg pl-10 pr-10 pt-6 pb-9 text-left overflow-hidden shadow-xl transform transition-all my-8 sm:align-middle',
	title: 'text-lg leading-6 font-medium text-gray-900',
	subTitle: 'text-gray-500 mt-1',
}

export default function EditTutorial() {
	const dispatch = useAppDispatch()

	const {
		modal: { modalName, modalInfo },
	} = useAppSelector((state) => state)

	const tutorialId = Number(modalInfo)

	const { data, isFetching } = tutorialApi.useShowQuery(
		{ id: tutorialId },
		{ skip: modalName !== 'editTutorial' || !tutorialId }
	)

	const [editTutorial, { isLoading, isSuccess }] = tutorialApi.useEditMutation()

	useEffect(() => {
		if (isSuccess) {
			dispatch(closeModal())
			dispatch(notificationCreators.show(successMessages.updatedTutorial))
		}
	}, [isSuccess, dispatch])

	const defaultValues: TutorialFormDefaults | undefined = useMemo(() => {
		if (!data) {
			return undefined
		}

		return {
			title: data.title,
			description: data.description || '',
			youtubeUrl: data.youtubeUrl,
			isPublished: data.isPublished,
			visibility: data.visibility,
			classIds: (data.classes || []).map((current) => ({
				value: current.id,
				label: current.name,
			})),
			rules: (data.rules || []).map((rule) => ({
				operator: rule.operator,
				value: rule.value,
			})),
		}
	}, [data])

	return (
		<Modal name='editTutorial'>
			<Dialog.Panel
				style={getCssStyle(
					{ name: 'overflowY', value: 'auto' },
					{ name: 'maxWidth', value: pixelsToRem(630) },
					{ name: 'maxHeight', value: pixelsToRem(640) },
					{ name: 'width', value: '100%' }
				)}
				className={`${styles.container} modal-scroll`}
			>
				<p className={styles.title}>Editar Tutorial</p>
				<p className={styles.subTitle}>
					As turmas e as regras são substituídas pelo que estiver aqui ao
					salvar.
				</p>

				{isFetching || !defaultValues ? (
					<div className='flex justify-center py-10'>
						<Spinner size='md' />
					</div>
				) : (
					<TutorialForm
						defaultValues={defaultValues}
						isLoading={isLoading}
						submitLabel='Salvar alterações'
						onCancel={() => dispatch(closeModal())}
						onSubmit={(payload: AddTutorialRequest) =>
							editTutorial({ id: tutorialId, ...payload })
						}
					/>
				)}
			</Dialog.Panel>
		</Modal>
	)
}
